import { useState, useEffect } from "react";
import { Calendar as CalendarIcon, Send, Sparkles, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { submitBooking } from "@/lib/api";
import { track } from "@/lib/tracker";

const EVENT_TYPES = [
  { value: "goochelshow", label: "Goochelshow" },
  { value: "tafelgoochelaar", label: "Tafelgoochelaar" },
  { value: "circusanimatie", label: "Circusanimatie" },
  { value: "suikerspin", label: "Suikerspin" },
  { value: "anders", label: "Iets anders / overleg" },
];

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "",
    guests: "",
    location: "",
    message: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Kies een datum voor het event.");
      return;
    }
    if (!form.event_type) {
      toast.error("Selecteer een type show.");
      return;
    }
    setSubmitting(true);
    try {
      await submitBooking({
        ...form,
        event_date: date.toISOString(),
      });
      track("booking_submit");
      setSuccess(true);
      toast.success("Aanvraag verzonden! Seppe neemt snel contact op.");
      setForm({
        name: "",
        email: "",
        phone: "",
        event_type: "",
        guests: "",
        location: "",
        message: "",
      });
      setDate(null);
    } catch (err) {
      console.error(err);
      toast.error("Oeps, er ging iets mis. Probeer opnieuw of bel via WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="boeken"
      className="relative py-24 sm:py-32 px-6 lg:px-10"
      data-testid="booking-section"
    >
      <div className="absolute inset-0 star-field opacity-30 pointer-events-none" />
      <div className="relative max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-gold mb-4">— Boek je show</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            Klaar voor <span className="gold-text italic">magie</span>?
          </h2>
          <p className="mt-6 text-lg text-[#B5A9C9]">
            Vul het formulier in en ontvang binnen 24 uur een persoonlijk voorstel.
            Geen verplichtingen.
          </p>
        </div>

        {success ? (
          <div
            className="glass-strong rounded-3xl p-12 text-center gold-glow"
            data-testid="booking-success"
          >
            <CheckCircle2 className="w-16 h-16 text-gold mx-auto mb-6" />
            <h3 className="font-display text-3xl font-bold mb-3">Aanvraag ontvangen!</h3>
            <p className="text-[#B5A9C9] mb-8">
              Bedankt — Seppe neemt zo snel mogelijk contact met je op via e-mail of telefoon.
            </p>
            <Button
              onClick={() => setSuccess(false)}
              className="bg-gold text-night-deep hover:bg-gold-light"
              data-testid="booking-new-request"
            >
              Nieuwe aanvraag
            </Button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="glass-strong rounded-3xl p-8 sm:p-12 space-y-6"
            data-testid="booking-form"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Naam" required>
                <Input
                  data-testid="booking-input-name"
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Jouw naam"
                  className="form-input"
                />
              </Field>
              <Field label="E-mail" required>
                <Input
                  data-testid="booking-input-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="jij@voorbeeld.be"
                  className="form-input"
                />
              </Field>
              <Field label="Telefoon" required>
                <Input
                  data-testid="booking-input-phone"
                  required
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+32 ..."
                  className="form-input"
                />
              </Field>
              <Field label="Type show" required>
                <Select
                  value={form.event_type}
                  onValueChange={(v) => update("event_type", v)}
                >
                  <SelectTrigger
                    data-testid="booking-select-event-type"
                    className="form-input"
                  >
                    <SelectValue placeholder="Kies een show" />
                  </SelectTrigger>
                  <SelectContent className="bg-night-surface border-gold/30 text-[#F8F5FF]">
                    {EVENT_TYPES.map((t) => (
                      <SelectItem
                        key={t.value}
                        value={t.value}
                        data-testid={`booking-event-option-${t.value}`}
                        className="focus:bg-gold/20 focus:text-[#F8F5FF]"
                      >
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Datum event" required>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="booking-date-trigger"
                      className={cn(
                        "form-input justify-start font-normal hover:bg-mystic/10",
                        !date && "text-[#7A6C96]"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-gold" />
                      {date ? format(date, "PPP", { locale: nl }) : "Kies datum"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0 bg-night-surface border-gold/30"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      locale={nl}
                      initialFocus
                      className="bg-night-surface text-[#F8F5FF]"
                    />
                  </PopoverContent>
                </Popover>
              </Field>
              <Field label="Aantal gasten">
                <Input
                  data-testid="booking-input-guests"
                  value={form.guests}
                  onChange={(e) => update("guests", e.target.value)}
                  placeholder="Bv. 25 kinderen"
                  className="form-input"
                />
              </Field>
            </div>

            <Field label="Locatie" required>
              <Input
                data-testid="booking-input-location"
                required
                value={form.location}
                onChange={(e) => update("location", e.target.value)}
                placeholder="Stad / plaats van het event"
                className="form-input"
              />
            </Field>

            <Field label="Bericht (optioneel)">
              <Textarea
                data-testid="booking-input-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Vertel iets over het event, wensen of specifieke vragen..."
                rows={5}
                className="form-input resize-none"
              />
            </Field>

            <div className="flex items-center justify-between gap-6 pt-2">
              <div className="flex items-center gap-2 text-xs text-[#7A6C96]">
                <Sparkles className="w-4 h-4 text-gold" />
                <span>Reactie binnen 24 uur</span>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit-button"
                className="bg-gold text-night-deep hover:bg-gold-light font-semibold px-8 py-6 rounded-full gold-glow-hover transition-all duration-300"
              >
                {submitting ? (
                  "Verzenden..."
                ) : (
                  <>
                    Verstuur aanvraag
                    <Send className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>

      <style>{`
        .form-input {
          background: rgba(22, 15, 36, 0.6) !important;
          border: 1px solid rgba(229, 185, 66, 0.25) !important;
          color: #F8F5FF !important;
          height: 48px;
        }
        .form-input:focus, .form-input:focus-visible {
          border-color: rgba(229, 185, 66, 0.8) !important;
          outline: none !important;
          box-shadow: 0 0 0 3px rgba(229, 185, 66, 0.15) !important;
        }
        textarea.form-input { height: auto !important; }
        .form-input::placeholder { color: #7A6C96; }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-[#B5A9C9]">
        {label}
        {required && <span className="text-gold ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
